import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Card, CardContent, Alert, Grid, Skeleton, Button, Select, MenuItem, TextField, FormControl, InputLabel, Paper, Stack, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Chip, Avatar } from '@mui/material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import MainLayout from '../layouts/MainLayout';
import { getRantingTransactions } from '../services/rantingService';

// Icons
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DescriptionIcon from '@mui/icons-material/Description';

const StatWidget = ({ title, value, icon, color, loading }) => (
  <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden', boxShadow: 3, borderRadius: 2 }}>
    <Box sx={{ position: 'absolute', top: -15, right: -15, opacity: 0.1, transform: 'rotate(-10deg) scale(2.5)', color: color }}>
        {icon}
    </Box>
    <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
        <Avatar sx={{ bgcolor: `${color}15`, color: color, width: 64, height: 64, mr: 2.5 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 0.5 }}>{title}</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: 'text.primary' }}>
                {loading ? <Skeleton width={120} /> : value}
            </Typography>
        </Box>
    </CardContent>
  </Card>
);

const RantingReportPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getRantingTransactions();
        setTransactions(data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter Data Client-Side
  const filteredTransactions = useMemo(() => {
    return transactions.filter(t => {
        const d = new Date(t.tanggal);
        const matchMonth = selectedMonth === 0 || (d.getMonth() + 1) === selectedMonth;
        const matchYear = d.getFullYear() === parseInt(selectedYear);
        return matchMonth && matchYear;
    }).sort((a, b) => new Date(b.tanggal) - new Date(a.tanggal));
  }, [transactions, selectedMonth, selectedYear]);

  // Calculate Aggregates
  const aggregates = useMemo(() => {
    return filteredTransactions.reduce((acc, t) => {
        const amount = parseFloat(t.jumlah);
        if (t.jenis === 'PEMASUKAN') {
            acc.pemasukan += amount;
            acc.byCategory[t.kategori?.nama || 'Lainnya'] = (acc.byCategory[t.kategori?.nama || 'Lainnya'] || 0) + amount;
        } else {
            acc.pengeluaran += amount;
        }
        return acc;
    }, { pemasukan: 0, pengeluaran: 0, byCategory: {} });
  }, [filteredTransactions]);

  const saldo = aggregates.pemasukan - aggregates.pengeluaran;

  const handleDownloadPdf = () => {
    const doc = new jsPDF();
    const monthName = selectedMonth === 0 ? 'Tahunan' : new Date(0, selectedMonth - 1).toLocaleString('id-ID', { month: 'long' });
    const title = `Laporan Keuangan Ranting - ${monthName} ${selectedYear}`;
    
    // Header
    doc.setFontSize(18); doc.setTextColor('#1565c0'); doc.text(title, 14, 20);
    doc.setFontSize(10); doc.setTextColor('#666'); doc.text(`Dicetak pada: ${new Date().toLocaleDateString('id-ID')}`, 14, 28);

    // Summary Table
    autoTable(doc, {
        head: [['Total Pemasukan', 'Total Pengeluaran', 'Saldo Akhir']],
        body: [[
            `Rp${aggregates.pemasukan.toLocaleString('id-ID')}`, 
            `Rp${aggregates.pengeluaran.toLocaleString('id-ID')}`, 
            `Rp${saldo.toLocaleString('id-ID')}`
        ]],
        startY: 35, theme: 'grid', headStyles: { fillColor: [46, 125, 50], halign: 'center' }, bodyStyles: { halign: 'center', fontStyle: 'bold' }
    });

    // Category Breakdown (Income)
    if (Object.keys(aggregates.byCategory).length > 0) {
        doc.text("Rincian Pemasukan per Kategori", 14, doc.lastAutoTable.finalY + 12);
        const categoryBody = Object.entries(aggregates.byCategory).map(([cat, val]) => [cat, `Rp${val.toLocaleString('id-ID')}`]);
        autoTable(doc, {
            head: [['Kategori', 'Jumlah']],
            body: categoryBody,
            startY: doc.lastAutoTable.finalY + 16, theme: 'striped', headStyles: { fillColor: [21, 101, 192] }
        });
    }

    // Transaction Details
    doc.addPage();
    doc.text("Rincian Riwayat Transaksi", 14, 20);
    autoTable(doc, {
        head: [['Tanggal', 'Kategori', 'Deskripsi', 'Masuk', 'Keluar']],
        body: filteredTransactions.map(t => [
            new Date(t.tanggal).toLocaleDateString('id-ID'),
            t.kategori?.nama || '-',
            t.deskripsi || '-',
            t.jenis === 'PEMASUKAN' ? `Rp${parseFloat(t.jumlah).toLocaleString('id-ID')}` : '-',
            t.jenis === 'PENGELUARAN' ? `Rp${parseFloat(t.jumlah).toLocaleString('id-ID')}` : '-'
        ]),
        startY: 25, theme: 'striped', headStyles: { fillColor: [156, 39, 176] }
    });

    doc.save(`Laporan_Ranting_${monthName}_${selectedYear}.pdf`);
  };

  if (error) return <MainLayout><Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box></MainLayout>;

  return (
    <MainLayout>
      <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
        
        {/* HEADER */}
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, display: 'flex', flexDirection: {xs:'column', md:'row'}, alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e0e0e0' }}>
            <Box>
                <Typography variant="h5" fontWeight="bold" color="primary.main">Laporan Kinerja Keuangan</Typography>
                <Typography variant="body2" color="text.secondary">Unduh laporan resmi kinerja ranting Anda.</Typography>
            </Box>
            <Stack direction={{xs:'column', sm:'row'}} spacing={2} sx={{ mt: {xs:2, md:0} }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Periode Bulan</InputLabel>
                    <Select value={selectedMonth} label="Periode Bulan" onChange={(e) => setSelectedMonth(e.target.value)}>
                        <MenuItem value={0}>Semua (Tahunan)</MenuItem>
                        {[...Array(12).keys()].map(m => (<MenuItem key={m + 1} value={m + 1}>{new Date(0, m).toLocaleString('id-ID', { month: 'long' })}</MenuItem>))}
                    </Select>
                </FormControl>
                <TextField size="small" label="Tahun" type="number" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} sx={{ width: 100 }} />
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownloadPdf} sx={{ borderRadius: 2 }}>Export PDF</Button>
            </Stack>
        </Paper>

        {/* HERO STATS */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <StatWidget title="Total Pemasukan" value={`Rp${aggregates.pemasukan.toLocaleString('id-ID')}`} icon={<TrendingUpIcon fontSize="large" />} color="#2e7d32" loading={loading} />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatWidget title="Total Pengeluaran" value={`Rp${aggregates.pengeluaran.toLocaleString('id-ID')}`} icon={<TrendingDownIcon fontSize="large" />} color="#d32f2f" loading={loading} />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatWidget title="Saldo Periode Ini" value={`Rp${saldo.toLocaleString('id-ID')}`} icon={<AccountBalanceWalletIcon fontSize="large" />} color="#1976d2" loading={loading} />
          </Grid>
        </Grid>

        {/* DETAIL TABLES */}
        <Grid container spacing={3}>
            {/* LEFT: Category Breakdown */}
            <Grid item xs={12} md={4}>
                <Card sx={{ boxShadow: 3, height: '100%', borderRadius: 2 }}>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <AssessmentIcon color="primary" />
                            <Typography variant="h6" fontWeight="bold">Sumber Dana</Typography>
                        </Stack>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Kategori</TableCell>
                                        <TableCell align="right">Jumlah</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(aggregates.byCategory).length === 0 ? (
                                        <TableRow><TableCell colSpan={2} align="center">Tidak ada data.</TableCell></TableRow>
                                    ) : (
                                        Object.entries(aggregates.byCategory).map(([cat, val]) => (
                                            <TableRow key={cat}>
                                                <TableCell>{cat}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                                    Rp{val.toLocaleString('id-ID')}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Grid>

            {/* RIGHT: Transaction History */}
            <Grid item xs={12} md={8}>
                <Card sx={{ boxShadow: 3, height: '100%', borderRadius: 2 }}>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <DescriptionIcon color="secondary" />
                            <Typography variant="h6" fontWeight="bold">Riwayat Transaksi Periode Ini</Typography>
                        </Stack>
                        <TableContainer sx={{ maxHeight: 500 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Tanggal</TableCell>
                                        <TableCell>Kategori</TableCell>
                                        <TableCell>Deskripsi</TableCell>
                                        <TableCell align="right">Nominal</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={4}><Skeleton /></TableCell></TableRow>) : 
                                        filteredTransactions.length === 0 ? <TableRow><TableCell colSpan={4} align="center">Tidak ada transaksi.</TableCell></TableRow> :
                                        filteredTransactions.map((t) => (
                                            <TableRow key={t.id} hover>
                                                <TableCell>{new Date(t.tanggal).toLocaleDateString('id-ID')}</TableCell>
                                                <TableCell><Chip label={t.kategori?.nama} size="small" variant="outlined" /></TableCell>
                                                <TableCell>{t.deskripsi}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold', color: t.jenis === 'PEMASUKAN' ? 'success.main' : 'error.main' }}>
                                                    {t.jenis === 'PEMASUKAN' ? '+' : '-'} {parseFloat(t.jumlah).toLocaleString('id-ID')}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>

      </Box>
    </MainLayout>
  );
};

export default RantingReportPage;